/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { useAINudges } from '@atlassian/ai-smart-button-api';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { useAISmartButtonContext } from '../../../../common/context';
import { isBrowser } from '../../../../common/utils/is-browser';
import { CONVO_STARTER_ID } from '../../../../constants';
import { useFetchNudgeRecommendations } from '../../../../hooks/useFetchNudgeRecommendations';
import type { MigratedBaseConversationAction } from '../../../../types';
import { findActiveNudge } from '../../../../utils/findActiveNudge';

import { useNudgeSessionState } from './use-nudge-session-state';

const messages = defineMessages({
	convoStarterSubheader: {
		id: 'ai-smart-button.convo-starter.nudge.subheader',
		defaultMessage: 'Open Rovo chat with this page-specific starter',
		description: 'Subheader text shown on hover for conversation starter nudges',
	},
});

// Lazy load to avoid loading useRovoActionTrigger when there's no active nudge
const LazyAIProactiveNudge = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-proactive-nudge" */ './main').then(
		(module) => ({
			default: module.AIProactiveNudge,
		}),
	),
);

type AIProactiveNudgeProps = {
	onActiveNudgeChange?: (hasActiveNudge: boolean) => void;
};

export const AIProactiveNudge = ({
	onActiveNudgeChange,
}: AIProactiveNudgeProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { product } = useAISmartButtonContext();
	const [triggerId] = useState(() => crypto.randomUUID());

	const selectedNudges = useAINudges();
	const shouldTrigger = Boolean(selectedNudges?.length);
	const hasPageCatchupNudge =
		selectedNudges?.some((nudge) => nudge.nudgeContext?.id === 'content-catchup') ?? false;
	const pageUrl = isBrowser() ? `${window.location.origin}${window.location.pathname}` : '';
	const { recommendations, loading, error } = useFetchNudgeRecommendations(
		triggerId,
		shouldTrigger,
		pageUrl,
		product,
		hasPageCatchupNudge,
	);
	const { acceptedNudges, dismissedNudges, addAcceptedNudge, addDismissedNudge } =
		useNudgeSessionState();

	const activeNudge: ConversationAction | MigratedBaseConversationAction | null = useMemo(() => {
		// TODO: Remove when new consumers can safely use recommendations without exceeding request rate limits
		// Temporary: added search to skip nudge BE
		if (product !== 'confluence' || pageUrl.includes('/wiki/search')) {
			return (
				selectedNudges.filter(
					(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
				)[0] ?? null
			);
		}

		if (loading || error || !recommendations?.length || !selectedNudges?.length) {
			return null;
		}
		// TODO: dismiss/accept nudge by its nudgeContext.id so we only need to check recommendations
		// Remove conversation starter if dismissed or accepted
		const availableRecommendations = recommendations.filter(
			(recommendation) =>
				!dismissedNudges.includes(recommendation.feature_details.feature_id) &&
				!acceptedNudges.includes(recommendation.feature_details.feature_id),
		);
		// Remove actions if dismissed or accepted
		const availableNudges = selectedNudges.filter(
			(nudge) => !dismissedNudges.includes(nudge.id) && !acceptedNudges.includes(nudge.id),
		);
		const nudge = findActiveNudge(availableRecommendations, availableNudges) ?? null;
		if (nudge?.id === CONVO_STARTER_ID) {
			return {
				...nudge,
				secondaryTitle: formatMessage(messages.convoStarterSubheader),
			};
		}
		return nudge;
	}, [
		acceptedNudges,
		dismissedNudges,
		error,
		loading,
		product,
		recommendations,
		selectedNudges,
		formatMessage,
		pageUrl,
	]);

	// Notify parent about active nudge state
	useEffect(() => {
		onActiveNudgeChange?.(Boolean(activeNudge));
	}, [activeNudge, onActiveNudgeChange]);

	// Early exit if no nudge to show
	if (!activeNudge) {
		return null;
	}

	return (
		<React.Suspense fallback={null}>
			<LazyAIProactiveNudge
				activeNudge={activeNudge}
				addAcceptedNudge={addAcceptedNudge}
				addDismissedNudge={addDismissedNudge}
			/>
		</React.Suspense>
	);
};
